import { lazy, Suspense, useEffect } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { track } from '@/lib/analytics';
import { openHelp } from '@/features/help/open';
import { InstallWatch } from '@/features/onboarding/install';
import { useBabySync } from '@/features/track/store';
import { useAutoClose } from '@/features/track/autoClose';
import { ageLabel } from '@/lib/format';
import { needsOnboarding, useSession } from '@/lib/session';
import { TabBar } from './TabBar';
import { Splash } from './Splash';
import { NoAccess } from './NoAccess';
import { setOnHome, useSkin } from '@/lib/skin';
import { SkyBackdrop } from '@/themes/glass/Sky';
import { MiniPlayer } from '@/features/sound/MiniPlayer';

const QuickLog = lazy(() => import('@/features/track/QuickLog'));

/** The glass logo: a two-leaf sprout. */
export function Sprout() {
  return (
    <svg viewBox="0 0 40 32" aria-hidden="true">
      <defs><linearGradient id="bp-leaf" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#9CCBFF" /><stop offset="1" stopColor="#3D86EE" /></linearGradient></defs>
      <path d="M20 30V17" stroke="#3D86EE" strokeWidth="2.4" strokeLinecap="round" fill="none" />
      <path d="M20 18C19 9 12 4 3 4c0 9 6 15 17 14Z" fill="url(#bp-leaf)" opacity="0.85" />
      <path d="M20 18c1-9 8-14 17-14 0 9-6 15-17 14Z" fill="url(#bp-leaf)" />
    </svg>
  );
}

/** Signed-in app: header (brand + baby), the tab's content, the tab bar. */
export function AppShell() {
  const s = useSession();
  const loc = useLocation();
  const { t } = useTranslation('common');
  const glass = useSkin() === 'glass';
  // Keep the offline tracker in sync on every tab, not only while Track is open.
  useBabySync(s.status === 'ready' ? s.baby?.id : undefined);
  // timers left on for days close themselves with a guessed end ("Check these entries" on Track)
  useAutoClose(s.status === 'ready' ? s.baby?.id : undefined, s.family?.timezone);

  // The sky follows the clock only on the home screen.
  useEffect(() => { setOnHome(loc.pathname === '/'); }, [loc.pathname]);

  // Screen views for every route are sent by app/Usage.tsx.

  // Opened from a reminder (the service worker adds ?from=push).
  useEffect(() => {
    if (s.status !== 'ready') return;
    const q = new URLSearchParams(loc.search);
    if (q.get('from') === 'push') {
      track('push_open', {}, 'notifications');
      q.delete('from');
      history.replaceState(null, '', loc.pathname + (q.toString() ? `?${q}` : ''));
      try { void (navigator as unknown as { clearAppBadge?: () => Promise<void> }).clearAppBadge?.(); } catch { /* unsupported */ }
    }
  }, [s.status, loc.search, loc.pathname]);

  if (s.status === 'loading') return <Splash />;
  if (s.status === 'signed-out') return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (s.status === 'no-access') return <NoAccess />;
  if (needsOnboarding(s)) return <Navigate to="/welcome" replace />;

  return (
    <div className="screen">
      {glass && <SkyBackdrop />}
      <InstallWatch />
      <AppHeader />
      <main className="screen-main">
        <Outlet />
      </main>
      <MiniPlayer />
      <TabBar />
      <Suspense fallback={null}><QuickLog /></Suspense>
    </div>
  );
}

/** The top bar on every signed-in page: the brand (home) and the baby (settings). */
export function AppHeader() {
  const s = useSession();
  const { t } = useTranslation('common');
  const glass = useSkin() === 'glass';
  return (
    <header className="top">
      <Link className="brand" to="/">{glass && <Sprout />}{t('brand')}</Link>
      {glass && s.baby ? (
        <div className="top-actions">
          <HelpButton />
          <Link className="who-pill lg" data-tour="family" to="/settings" aria-label={t('header.openSettings', { name: s.baby.name })}>
            <span className="who"><b>{s.baby.name}</b><small>{ageLabel(s.baby.age)}</small></span>
            <span className="ava" aria-hidden="true">{s.baby.name.slice(0, 1).toUpperCase()}</span>
          </Link>
        </div>
      ) : (
        <div className="top-right">
          <HelpButton />
          {s.baby && (
            <Link className="baby-link" to="/settings/baby" aria-label={t('header.openBaby', { name: s.baby.name })}>
              {s.baby.name} · {ageLabel(s.baby.age)}
            </Link>
          )}
          <ProfileButton />
        </div>
      )}
    </header>
  );
}

/** Small "?" in the top bar: help articles, feedback, email support. Quiet on purpose. */
export function HelpButton() {
  const { t } = useTranslation('help');
  return (
    <button className="help-btn" type="button" data-track="top_help" aria-label={t('button')} onClick={() => openHelp('top_bar')}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><path d="M12 17h.01" /></svg>
    </button>
  );
}

/** Round "You" button: profile, family, settings. */
export function ProfileButton() {
  const { t } = useTranslation('common');
  return (
    <Link className="profile-btn" to="/settings" aria-label={t('header.profile')}>
      <span><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="4" /><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" /></svg></span>
    </Link>
  );
}

/** Full-screen pages without the tab bar (auth, onboarding, settings, invites). `header`: show the app's top bar
 *  once signed in (settings), so the brand and the baby stay at the top as on the tabs. */
export function PageShell({ header = false }: { header?: boolean }) {
  const glass = useSkin() === 'glass';
  const s = useSession();
  return (
    <div className="screen">
      {glass && <SkyBackdrop />}
      {header && s.status === 'ready' && <AppHeader />}
      <Outlet />
    </div>
  );
}
